import type {
  InputDescription,
  IntegrationDescription,
  Member,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import {
  AvatarMenuOption,
  DropdownPanel,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
  Text,
} from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import type { MutableRefObject, Ref } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { PickerStyleWrapper } from "../../../../../components/PickerStyleWrapper";
import { useFieldMissingValidation } from "../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import { ActionLabel } from "../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../Sections/ErrorSection";
import { StyledNoFields } from "../FieldEditor/FieldEditorRow/containers/TriggerOptions/style";
import { UpdateValueDropdown } from "../FieldEditor/FieldEditorRow/containers/UpdateValueDropdown";
import { OperatorDescriptionKinds } from "../FieldEditor/FieldEditorRow/containers/UpdateValueDropdown/constanst";
import {
  checkIfIsPickerInputField,
  ifLoopingSourceIsActionFind,
} from "../FieldEditor/FieldEditorRow/fields/utils";
import { POSITION_AT, POSITION_MY } from "../style";
import { nameToInitials } from "../utils";
import { generalTypes, ssFields, ssTypes } from "./string-output-types";
import { Root, StyledReactInputFieldContainer } from "./style";
import type { TriggerActionOption } from "./utils";
import type { ActionOptionGroup } from "../utils/action-options";
import {
  useConverters,
  ejectPillFromInput,
  mergeNextPillToInput,
  triggerToOptions,
} from "./utils";
import { actionsToOptions } from "../utils/action-options";
import { SolutionColorVariables } from "@smartsuite/types";
import { memberContextSpecificFields } from "../EmailEditor/string-output-types";
import { CurrentOption } from "../FieldEditor/FieldEditorRow/containers/CurrentOption/CurrentOption";
import { useLoopingMenu } from "../hooks/use-looping-menu";
import { useActionMemberDirectory } from "../../../../../hooks/use-member-directory";
import { handleTriggerOutput } from "../EmailEditor/utils";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../utils/action-sources";
import type { LoopScope } from "../../../../../stores/automation/looping.store";

export enum MemberTab {
  root = "root",
  fields = "fields",
  members = "members",
  actions = "actions",
  teams = "teams",
  current = "current",
}

export const MemberEditor = observer<{
  config: InputCandidate;
  description?: InputDescription;
  trigger: TriggerCandidate;
  onChange: (input: InputCandidate) => void;
  showValidationError: boolean;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  integrations: IntegrationDescription[];
  isMultiple: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  hideTeams?: boolean;
  filterTriggerFields?: (option: TriggerActionOption) => boolean;
  filterActionFields?: (option: ActionOptionGroup) => boolean;
  defaultTab?: MemberTab;
  isNested?: boolean;
  disableSplit?: boolean;
}>(
  ({
    config,
    description,
    trigger,
    onChange,
    showValidationError,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    integrations,
    isMultiple,
    isError,
    onClickTrash,
    hideTeams,
    filterTriggerFields,
    filterActionFields,
    defaultTab,
    isNested,
  }) => {
    const { t } = useTranslation();

    const store = useRootStore();

    // Scoped to the solution this action writes into: a record's user field may
    // only hold members who can actually see that record.
    const { members, teams } = useActionMemberDirectory(action);

    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<MemberTab>(defaultTab ?? MemberTab.fields);
    const [triggerFields, setTriggerFields] = useState<TriggerActionOption[]>(
      [],
    );
    const [actionFields, setActionFields] = useState<ActionOptionGroup[]>([]);
    const [selectedAction, setSelectedAction] = useState<
      ActionOptionGroup | undefined
    >();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [validationError, setValidationError] = useState<string>("");
    const [focus, setFocus] = useState<boolean>(false);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );

    const { convertInputToPills } = useConverters();

    const { loopingMenu, loopingFields, loopingAction } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.STRING],
      ssFieldTypes: ssFields,
      systemAllowedOptions: [],
      action,
    });

    useEffect(() => {
      if (showValidationError) {
        if (R.isTruthy(description)) {
          setValidationError(checkInputValidation(description, config ?? []));
        }
      } else {
        setValidationError("");
      }
    }, [config, description, showValidationError]);

    useEffect(() => {
      const options = triggerToOptions(
        trigger.description,
        generalTypes,
        ssTypes,
        ssFields,
        memberContextSpecificFields,
      );

      if (R.isTruthy(filterTriggerFields)) {
        setTriggerFields(options.filter(filterTriggerFields));
      } else {
        setTriggerFields(options);
      }
    }, [trigger.description]);

    useEffect(() => {
      const options = canOfferActionSources(action, actionsArePreScoped)
        ? actionsToOptions(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,
            [ValueDescription_T.STRING],
            ssFields,
            [],
            looping,
          )
        : [];

      if (R.isTruthy(filterActionFields)) {
        setActionFields(options.filter(filterActionFields));
      } else {
        setActionFields(options);
      }
    }, [actions, action, actionsArePreScoped, loopScope]);

    useEffect(() => {
      if (R.isTruthy(defaultTab)) {
        setTab(defaultTab);
      } else if (triggerFields.length && triggerOutputs.length > 0) {
        setTab(MemberTab.fields);
      } else if (members.length) {
        setTab(MemberTab.members);
      } else {
        setTab(MemberTab.root);
      }
    }, [triggerFields, members]);

    useEffect(() => {
      if (
        store.inputToFocus?.inputId === config.inputId &&
        description?.editor?.editor.oneofKind === "member" // do not open dropdown for checklist
      ) {
        setIsOpen(true);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      if (actionType === undefined) return;
      if (actionType === OperatorDescriptionKinds.CLEAR) {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
      onChange({
        ...config,
        value:
          actionType === OperatorDescriptionKinds.CLEAR
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, isOpen]);

    const pillList = useMemo(
      () =>
        convertInputToPills(
          config,
          members,
          teams,
          trigger,
          actions,
          triggerFields,
          actionFields,
          action,
          loopingFields,
        ),
      [config, members, teams, triggerFields, actionFields, loopingFields],
    );

    const filteredMembers = useMemo(() => {
      return members
        .filter(
          (member) =>
            !pillList.some(
              (pill) =>
                pill.type === "member" && pill.value === member.memberId,
            ),
        )
        .filter((member) =>
          getMemberName(member)
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        );
    }, [members, pillList, searchQuery]);

    const unselectedTriggerFields = triggerFields.filter(
      (option) =>
        !pillList.some(
          (pill) =>
            pill.type === "triggerOutput" && pill.value === option.outputId,
        ),
    );
    const unselectedActionFields =
      selectedAction?.options.filter(
        (option) =>
          !pillList.some(
            (pill) =>
              pill.type === "actionOutput" && pill.value === option.outputId,
          ),
      ) ?? [];

    const unselectedLoopFields =
      loopingFields?.options.filter(
        (option) =>
          !option.contextSpecific &&
          !pillList.some((pill) => pill.value === option.outputId),
      ) ?? [];

    const unselectedLoopFieldsFiltered = unselectedLoopFields.filter(
      (option) =>
        searchQuery.length > 0
          ? option.title.toLowerCase().includes(searchQuery.toLowerCase())
          : true,
    );

    const triggerOutputs = unselectedTriggerFields.filter((option) =>
      searchQuery.length > 0
        ? option.title.toLowerCase().includes(searchQuery.toLowerCase())
        : true,
    );

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    return (
      <Root>
        <StyledReactInputFieldContainer>
          {description !== undefined && (
            <CaptionBlock
              description={description}
              onClickTrash={onClickTrash}
            />
          )}
          <PickerStyleWrapper>
            <PickerInputField
              showAddButton={!isCleanAction}
              className={
                (isError ?? !isInputValidByFieldMissing(config, action))
                  ? `is-error picker-input-field-${config.inputId} ${
                      isCleanAction ? "is-clear" : ""
                    }`
                  : `picker-input-field-${config.inputId} ${
                      isCleanAction ? "is-clear" : ""
                    }`
              }
              // @ts-expect-error
              buttonRef={buttonRef as Ref<HTMLButtonElement>}
              state={
                (isError ?? !isInputValidByFieldMissing(config, action))
                  ? "error"
                  : isOpen || focus
                    ? "active"
                    : "default"
              }
              isOpen={isCleanAction ? false : isOpen}
              pills={pillList}
              pillStyle={{
                textSize: "m",
              }}
              onButtonClick={() => {
                setIsOpen(true);
              }}
              onPillRemove={(index) => {
                onChange(ejectPillFromInput(config, index));
              }}
              allowManualInput={false}
              placeholder={
                isCleanAction
                  ? t("automation-form.clean-operator-editor-placeholder")
                  : isOpen || focus
                    ? pillList.length > 0
                      ? ""
                      : t("editor.select-or-use-field")
                    : ""
              }
              onClick={() => {
                description?.editor?.hidePopupOnHover !== true &&
                  setIsOpen(true);
              }}
            >
              {action?.actionReference?.actionId === ActionLabel.updateRecord &&
                description?.operators?.operators !== undefined &&
                description?.operators?.operators.length > 0 &&
                !isNested && (
                  <UpdateValueDropdown
                    operatorConfig={config.operatorConfig}
                    operatorDescriptions={description?.operators.operators}
                    onOpenedChange={setFocus}
                    onChange={changeActionType}
                  />
                )}

              {isOpen && (
                <DropdownPanel
                  targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
                  clickOutsideExcept={[".dropdown-panel"]}
                  isActive={isOpen}
                  at={POSITION_AT}
                  my={POSITION_MY}
                  minWidth={300}
                  arrowBackground="custom"
                  arrowCustomBackground={SolutionColorVariables.sc1}
                  panelLineBackground={SolutionColorVariables.sc1}
                  portalStyles={getLocalCssColorVariables(store.theme)}
                  onClickOutside={(event) => {
                    if (
                      !checkIfIsPickerInputField(event.target, config.inputId)
                    ) {
                      setIsOpen(false);
                    }
                  }}
                  onCloseAnimationEnd={() => {
                    setSearchQuery("");
                  }}
                >
                  {tab === "root" && (
                    <MenuOptionWrapper>
                      {triggerOutputs.length > 0 && (
                        <MenuOption
                          submenuArrow="right"
                          onClick={() => setTab(MemberTab.fields)}
                        >
                          {t("automation-form.members-from-trigger")}
                        </MenuOption>
                      )}
                      {actionFields.map(
                        (actionMenu) =>
                          actionMenu?.options.length > 0 && (
                            <MenuOption
                              key={actionMenu.action.hash}
                              submenuArrow="right"
                              onClick={() => {
                                setSelectedAction(actionMenu);
                                setTab(MemberTab.actions);
                              }}
                            >
                              {actionMenu.hideNamePrefix === true
                                ? actionMenu.name
                                : t("field-popups.action-popup", {
                                    name: actionMenu.name,
                                  })}
                            </MenuOption>
                          ),
                      )}
                      {members.length > 0 && (
                        <MenuOption
                          submenuArrow="right"
                          onClick={() => setTab(MemberTab.members)}
                        >
                          {t("editor.members")}
                        </MenuOption>
                      )}
                      {!hideTeams && teams.length > 0 && (
                        <MenuOption
                          submenuArrow="right"
                          onClick={() => setTab(MemberTab.teams)}
                        >
                          {t("editor.teams")}
                        </MenuOption>
                      )}
                      <CurrentOption
                        loopingMenu={loopingMenu}
                        loopScope={looping}
                        actionsArePreScoped={actionsArePreScoped}
                        addPill={(looping) => {
                          // Case when source for looping is the find action
                          if (
                            ifLoopingSourceIsActionFind(looping) ||
                            loopingMenu?.options?.length
                          ) {
                            setTab(MemberTab.current);
                            return;
                          }

                          if (looping.source) {
                            setTimeout(() => setIsOpen(false));
                            onChange(
                              mergeNextPillToInput(
                                config,
                                {
                                  type: "loop",
                                  looping,
                                },
                                isNextPillAllowed(config, isMultiple),
                              ),
                            );
                          }
                        }}
                      />
                    </MenuOptionWrapper>
                  )}

                  {tab === "fields" && (
                    <MenuGroupContent
                      searchPlaceholder={t("automation-form.search-fields")}
                      title={t("automation-form.members-from-trigger")}
                      search={unselectedTriggerFields.length > 5}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab(MemberTab.root);
                        setSearchQuery("");
                      }}
                    >
                      {triggerOutputs
                        .filter((item) => !item.contextSpecific)
                        .map((option, index) => (
                          <MenuOption
                            key={`triggerOption-${index}`}
                            onClick={() => {
                              if (!isMultiple) {
                                setTimeout(() => setIsOpen(false));
                              }
                              if (R.isTruthy(trigger.triggerReference)) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "triggerOutput",
                                      triggerReference:
                                        trigger.triggerReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text>{option.title}</Text>
                          </MenuOption>
                        ))}
                      {unselectedTriggerFields.length === 0 && (
                        <StyledNoFields>No results</StyledNoFields>
                      )}
                      {unselectedTriggerFields.filter(
                        (option) => !option.contextSpecific,
                      ).length > 0 &&
                        unselectedTriggerFields.filter(
                          (option) => option.contextSpecific,
                        ).length > 0 && <MenuLineSeparator />}

                      {triggerOutputs
                        .filter((item) => item.contextSpecific)
                        .map((option, index) => (
                          <MenuOption
                            key={`triggerOption-${index}`}
                            onClick={() => {
                              if (!isMultiple) {
                                setTimeout(() => setIsOpen(false));
                              }
                              if (R.isTruthy(trigger.triggerReference)) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "triggerOutput",
                                      triggerReference:
                                        trigger.triggerReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text>{option.title}</Text>
                          </MenuOption>
                        ))}
                    </MenuGroupContent>
                  )}

                  {tab === "members" && (
                    <MenuGroupContent
                      title={t("editor.members")}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab(MemberTab.root);
                        setSearchQuery("");
                      }}
                    >
                      {filteredMembers.map((member) => (
                        <AvatarMenuOption
                          key={member.memberId}
                          onClick={() => {
                            if (!isMultiple) {
                              setTimeout(() => setIsOpen(false));
                            }
                            onChange(
                              mergeNextPillToInput(
                                config,
                                {
                                  type: "member",
                                  memberId: member.memberId,
                                },
                                isNextPillAllowed(config, isMultiple),
                              ),
                            );
                          }}
                          initials={nameToInitials(getMemberName(member))}
                          image={member.profileImageUrl}
                        >
                          {getMemberName(member)}
                        </AvatarMenuOption>
                      ))}
                      {filteredMembers.length === 0 && (
                        <StyledNoFields>No results</StyledNoFields>
                      )}
                    </MenuGroupContent>
                  )}

                  {tab === "teams" && !hideTeams && (
                    <MenuGroupContent
                      title={t("editor.teams")}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab(MemberTab.root);
                        setSearchQuery("");
                      }}
                    >
                      {teams
                        .filter(
                          (team) =>
                            !pillList.some(
                              (pill) =>
                                pill.type === "team" &&
                                pill.value === team.teamId,
                            ),
                        )
                        .filter((team) =>
                          team.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                        )
                        .map((team) => (
                          <AvatarMenuOption
                            key={team.teamId}
                            onClick={() => {
                              if (!isMultiple) {
                                setTimeout(() => setIsOpen(false));
                              }
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "team",
                                    teamId: team.teamId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }}
                            initials={nameToInitials(team.name)}
                          >
                            {team.name}
                          </AvatarMenuOption>
                        ))}
                    </MenuGroupContent>
                  )}

                  {tab === "current" && loopingFields !== undefined && (
                    <MenuGroupContent
                      title={t("general.current-item")}
                      search={unselectedLoopFields.length > 5}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab(MemberTab.root);
                        setSearchQuery("");
                      }}
                    >
                      {unselectedLoopFieldsFiltered.map((option, index) => (
                        <MenuOption
                          key={`selectedActionOption-${index}`}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });

                            if (loopingAction?.actionReference) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      loopingAction?.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                              return;
                            }

                            const triggerConfig = handleTriggerOutput(
                              trigger,
                              option,
                            );
                            if (triggerConfig) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  triggerConfig,
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                      {unselectedLoopFieldsFiltered.length === 0 && (
                        <StyledNoFields>No results</StyledNoFields>
                      )}
                    </MenuGroupContent>
                  )}

                  {tab === "actions" && selectedAction !== undefined && (
                    <MenuGroupContent
                      title={t("field-popups.action-popup", {
                        name: selectedAction.name,
                      })}
                      search={unselectedActionFields.length > 5}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab(MemberTab.root);
                        setSearchQuery("");
                      }}
                    >
                      {unselectedActionFields
                        .filter((item) => !item.contextSpecific)
                        .filter((option) =>
                          searchQuery.length > 0
                            ? option.title
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())
                            : true,
                        )
                        .map((option, index) => (
                          <MenuOption
                            key={`selectedActionOption-${index}`}
                            onClick={() => {
                              if (
                                R.isTruthy(
                                  selectedAction.action.actionReference,
                                )
                              ) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "actionOutput",
                                      actionReference:
                                        selectedAction.action.actionReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text styles={{ marginLeft: "13px" }}>
                              {option.title}
                            </Text>
                          </MenuOption>
                        ))}
                      {unselectedActionFields.filter(
                        (option) => option.contextSpecific,
                      ).length > 0 &&
                        unselectedActionFields.filter(
                          (option) => option.contextSpecific,
                        ).length > 0 && <MenuLineSeparator />}
                      {unselectedActionFields
                        .filter((item) => item.contextSpecific)
                        .filter((option) =>
                          searchQuery.length > 0
                            ? option.title
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())
                            : true,
                        )
                        .map((option, index) => (
                          <MenuOption
                            key={`selectedActionOption-${index}`}
                            onClick={() => {
                              if (
                                R.isTruthy(
                                  selectedAction.action.actionReference,
                                )
                              ) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "actionOutput",
                                      actionReference:
                                        selectedAction.action.actionReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text styles={{ marginLeft: "13px" }}>
                              {option.title}
                            </Text>
                          </MenuOption>
                        ))}
                    </MenuGroupContent>
                  )}
                </DropdownPanel>
              )}
            </PickerInputField>
          </PickerStyleWrapper>
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError
            message={getFieldMissingErrorMessage(config, action)}
          />
        </StyledReactInputFieldContainer>
      </Root>
    );
  },
);

const getMemberName = (member: Member): string =>
  member.name.length > 0 ? member.name : (member.email[0] ?? "");

const isNextPillAllowed = (
  config: InputCandidate,
  isMultiple: boolean,
): boolean => {
  return !(!isMultiple && config.value?.values.length === 1);
};

MemberEditor.displayName = "MemberEditor";
